import { useSuspenseQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import {
  BookmarkPage,
  BookmarkPageQuery,
  BookmarkPageQueryVariables,
  QUERY_BOOKMARK_PAGE,
} from '@edwin-group/people-graphql';
import { BookmarkTab, useBookmarkFilters } from '@/components/Bookmark/useBookmark';
import { Tab } from '@/components/ui/tabs';

const DEFAULT_TAB_LABELS: Record<BookmarkTab, string> = {
  KNOWLEDGE_BASE: 'Knowledge base',
  PODCAST: 'Podcast',
  COURSE: 'Learn',
  RESOURCE: 'Resource',
};

interface ExtendedBookmarkPage extends BookmarkPage {
  searchPlaceholder?: string | null;
  loadingText?: string | null;
  emptyStateText?: string | null;
  removeFromBookmarksText?: string | null;
  goToResourceText?: string | null;
  tabKnowledgeBase?: string | null;
  tabPodcast?: string | null;
  tabLearn?: string | null;
  tabResource?: string | null;
}

export function useBookmarkPage() {
  const { data } = useSuspenseQuery<BookmarkPageQuery, BookmarkPageQueryVariables>(QUERY_BOOKMARK_PAGE, {
    fetchPolicy: 'cache-first',
  });
  const bookmarkPage = data?.bookmarkPage as ExtendedBookmarkPage | undefined;

  const tabLabels = useMemo<Record<BookmarkTab, string>>(
    () => ({
      KNOWLEDGE_BASE: bookmarkPage?.tabKnowledgeBase || DEFAULT_TAB_LABELS.KNOWLEDGE_BASE,
      PODCAST: bookmarkPage?.tabPodcast || DEFAULT_TAB_LABELS.PODCAST,
      COURSE: bookmarkPage?.tabLearn || DEFAULT_TAB_LABELS.COURSE,
      RESOURCE: bookmarkPage?.tabResource || DEFAULT_TAB_LABELS.RESOURCE,
    }),
    [bookmarkPage?.tabKnowledgeBase, bookmarkPage?.tabPodcast, bookmarkPage?.tabLearn, bookmarkPage?.tabResource],
  );

  const {
    allBookmarks,
    filteredBookmarks,
    availableCategories,
    isLoading,
    filters,
    pagination,
    searchValue,
    onSearchChange,
    onCategoryChange,
    onPageChange,
    refetch,
  } = useBookmarkFilters();

  useEffect(() => {
    if (!filters.category && availableCategories.length > 0) {
      onCategoryChange(availableCategories[0]);
    }
  }, [availableCategories, filters.category, onCategoryChange]);

  useEffect(() => {
    if (filteredBookmarks.length === 0 && availableCategories.length > 0) {
      onCategoryChange(availableCategories[0]);
    }
  }, [filteredBookmarks.length, availableCategories, onCategoryChange]);

  const tabs = useMemo<Tab[]>(
    () =>
      availableCategories.map((category) => ({
        name: tabLabels[category],
        current: filters.category === category,
        onClick: () => onCategoryChange(category),
      })),
    [availableCategories, filters.category, onCategoryChange, tabLabels],
  );

  const handleTabChange = useCallback(
    (newTabs: Tab[]) => {
      const selectedTab = newTabs.find((t) => t.current);
      if (selectedTab) {
        const category = availableCategories.find((cat) => tabLabels[cat] === selectedTab.name);
        if (category) {
          onCategoryChange(category);
        }
      }
    },
    [onCategoryChange, availableCategories, tabLabels],
  );

  return {
    bookmarkPage,
    allBookmarks,
    filteredBookmarks,
    availableCategories,
    isLoading,
    filters,
    pagination,
    searchValue,
    onSearchChange,
    onCategoryChange,
    onPageChange,
    refetch,
    tabs,
    handleTabChange,
  };
}
